import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, ArrowRight, GraduationCap, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { analytics } from '../hooks/useAnalytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GREETING = {
  role: 'assistant',
  content: "Hi there! I'm the Ivy League Consulting advisor. Whether you're a student or a parent, I'm here to help you navigate the admissions process. What brings you here today?",
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [pulse, setPulse] = useState(true);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // Stop pulse after first open
  const handleOpen = useCallback(() => {
    setOpen(true);
    setPulse(false);
    analytics.chatOpen();
  }, []);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      // Build history for context (skip the greeting)
      const history = newMessages
        .filter((m) => m !== GREETING)
        .map((m) => ({ role: m.role, content: m.content }));

      const res = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          session_id: sessionId,
          history: history.slice(-10),
        }),
      });

      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setSessionId(data.session_id);
      setMessages((prev) => [...prev, { role: 'assistant', content: data.response }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: "I'm sorry, I'm having trouble connecting right now. Please try again in a moment, or reach out to us at taimur@ivyleaguehelp.com or call (202) 731-3100." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat Window */}
      {open && (
        <div
          className="fixed bottom-56 right-4 md:bottom-28 sm:right-6 z-[55] w-[360px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-[#E2E2E2] flex flex-col overflow-hidden"
          style={{ height: '520px' }}
          data-testid="chat-widget-window"
        >
          {/* Header */}
          <div className="bg-[#0F392B] px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-[#D4AF37]" strokeWidth={1.5} />
              <div>
                <p className="text-sm font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Ivy League Consulting</p>
                <p className="text-[10px] text-white/60">Admissions Advisor</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white transition-colors" data-testid="chat-close-btn">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-[#F9F9F7]" data-testid="chat-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-[#0F392B] text-white rounded-br-md'
                      : 'bg-white border border-[#E2E2E2] text-foreground rounded-bl-md'
                  }`}
                  data-testid={`chat-message-${i}`}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-[#E2E2E2] rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 bg-[#0F392B]/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-1.5 h-1.5 bg-[#0F392B]/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-1.5 h-1.5 bg-[#0F392B]/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Quick Actions (show only at start) */}
          {messages.length <= 1 && (
            <div className="px-4 py-2 border-t border-[#E2E2E2] bg-white flex-shrink-0" data-testid="chat-quick-actions">
              <div className="flex flex-wrap gap-1.5">
                {[
                  "I'm a student exploring options",
                  "I'm a parent looking for help",
                  "What makes you different?",
                  "Tell me about your packages",
                ].map((q, i) => (
                  <button
                    key={i}
                    onClick={() => { setInput(q); }}
                    className="text-[11px] px-2.5 py-1.5 rounded-full border border-[#0F392B]/15 text-[#0F392B] hover:bg-[#0F392B]/5 transition-colors"
                    data-testid={`chat-quick-${i}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="px-3 py-2.5 border-t border-[#E2E2E2] bg-white flex items-center gap-2 flex-shrink-0">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask anything about admissions..."
              className="flex-1 text-sm px-3 py-2 rounded-lg bg-[#F9F9F7] border border-[#E2E2E2] focus:outline-none focus:ring-1 focus:ring-[#0F392B]/20 focus:border-[#0F392B]"
              disabled={loading}
              data-testid="chat-input"
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || loading}
              className="w-9 h-9 rounded-lg bg-[#0F392B] hover:bg-[#0F392B]/90 text-white flex items-center justify-center disabled:opacity-40 transition-colors flex-shrink-0"
              data-testid="chat-send-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
        </div>
      )}

      {/* Floating Toggle Button */}
      <button
        onClick={open ? () => setOpen(false) : handleOpen}
        className={`fixed bottom-36 right-4 md:bottom-12 sm:right-6 z-[55] w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-300 ${
          open ? 'bg-[#0F392B] rotate-0' : 'bg-[#0F392B] hover:scale-110'
        }`}
        data-testid="chat-toggle-btn"
        aria-label="Chat with admissions advisor"
      >
        {open ? (
          <X className="h-6 w-6 text-white" />
        ) : (
          <>
            <MessageCircle className="h-6 w-6 text-white" />
            {pulse && (
              <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-[#D4AF37] rounded-full animate-ping" />
            )}
            {pulse && (
              <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-[#D4AF37] rounded-full" />
            )}
          </>
        )}
      </button>
    </>
  );
}
